/**
 * API client calls for the /api/reference-imports/* surface.
 *
 * All functions use apiClient (which attaches the Supabase JWT token).
 * Types mirror the backend Pydantic models in models/reference_media.py
 * and the route response shapes in api/reference_imports_routes.py.
 *
 * Spec: docs/superpowers/specs/2026-06-30-disco-playlist-import-design.md §6.2
 * Plan: docs/superpowers/plans/2026-06-30-disco-playlist-import-implementation.md P3-T3
 */

import apiClient, { ApiError } from "@/lib/api-client";

// ── Feature-flag (kill switch) helpers ────────────────────────────────────────
// The backend gates the whole /api/reference-imports/* surface (plus the reference
// signed-audio branch and anon share reads) behind the GLOBAL ENABLE_DISCO_IMPORT
// flag. When it is off, every one of these calls returns HTTP 503. The UI must
// treat that as "feature not enabled" rather than a hard error.

/** User-facing copy shown when Disco import is globally disabled. */
export const REFERENCE_IMPORT_DISABLED_MESSAGE =
  "Disco import isn't enabled yet. Please contact your administrator.";

/** True when an error is the 503 kill-switch response (feature globally off). */
export function isReferenceImportDisabled(err: unknown): boolean {
  return err instanceof ApiError && err.status === 503;
}

// ── Per-track data returned by /disco/preview ─────────────────────────────────

export interface DiscoPreviewTrack {
  /** Disco track ID (integer). Named track_id in the FINAL backend contract (I2). */
  track_id: number;
  name: string;
  artist: string;
  album: string;
  duration: number;
  ext: string;
  pretty_size: string;
  /** Raw byte size of the audio file. Used for L6 per-track pre-flight check (I2). */
  size_bytes: number;
  track_type: string;
  video: boolean;
  virus: boolean;
  outside_your_territories: boolean;
  /** ISRC from Disco metadata (I2). */
  isrc: string | null;
}

// ── POST /api/reference-imports/disco/preview ─────────────────────────────────

export interface DiscoPreviewResponse {
  /** Playlist name as returned by the Disco share page. */
  name: string;
  /**
   * Cover image URL. NOTE: the DB column is `playlists.cover_image_url`
   * (no `image_url` column). The backend maps `cover_url` to this.
   * The preview payload calls it `image_url` for display convenience only.
   */
  image_url: string | null;
  track_count: number;
  /** Whether the share allows audio download. Pre-flight: if false, disable confirm. (L6) */
  downloadable: boolean;
  /**
   * Share mode string from Disco. The backend validates 'standard' only.
   * The dialog uses this for L6 pre-flight: if mode != 'standard', warn the user.
   */
  share_mode: string;
  /** Sum of all per-track size_bytes. Pre-flight L7: compare to 10 GB. */
  total_bytes_estimate: number;
  tracks: DiscoPreviewTrack[];
}

export async function previewDiscoImport(url: string): Promise<DiscoPreviewResponse> {
  return apiClient.post<DiscoPreviewResponse>(
    "/api/reference-imports/disco/preview",
    { url }
  );
}

// ── POST /api/reference-imports/disco/ingest ─────────────────────────────────

export interface IngestResponse {
  playlist_id: string;
  import_status: "downloading";
}

/**
 * Start a Disco import.
 *
 * @param selectedTrackIds Disco track ids (DiscoPreviewTrack.track_id) to import.
 *   Omit / undefined → import the whole playlist (backwards-compatible). An explicit
 *   array imports ONLY those tracks; the rest are never downloaded, analysed, or
 *   counted against quota. The backend persists it to playlists.import_payload so a
 *   retry honours the same selection.
 */
export async function ingestDiscoImport(
  url: string,
  selectedTrackIds?: number[]
): Promise<IngestResponse> {
  const body: { url: string; selected_track_ids?: number[] } = { url };
  if (selectedTrackIds !== undefined) {
    body.selected_track_ids = selectedTrackIds;
  }
  return apiClient.post<IngestResponse>(
    "/api/reference-imports/disco/ingest",
    body
  );
}

// ── GET /api/reference-imports/playlists ─────────────────────────────────────

export interface ReferencePlaylistListItem {
  id: string;
  name: string;
  cover_image_url: string | null;
  source: "disco_import";
  import_status: string | null;
  import_error: string | null;
  organization_id: string;
  source_url: string | null;
  user_id: string;
  created_at: string;
  updated_at: string;
}

export interface ListPlaylistsResponse {
  items: ReferencePlaylistListItem[];
  total: number;
  has_more: boolean;
}

export async function listReferencePlaylists(params?: {
  limit?: number;
  offset?: number;
}): Promise<ListPlaylistsResponse> {
  const limit = params?.limit ?? 50;
  const offset = params?.offset ?? 0;
  return apiClient.get<ListPlaylistsResponse>(
    `/api/reference-imports/playlists?limit=${limit}&offset=${offset}`
  );
}

// ── GET /api/reference-imports/playlists/{id} ─────────────────────────────────

export interface ReferenceTrack {
  id: string;
  organization_id: string;
  title: string;
  artist: string | null;
  album: string | null;
  release_date: string | null;
  duration_sec: number | null;
  isrc: string | null;
  audio_storage_path: string | null;
  audio_format: string | null;
  source: string;
  source_track_id: string | null;
  source_metadata: Record<string, unknown> | null;

  // Analysis columns (mirrors tracks table 1:1)
  bpm: number | null;
  tempo: string | null;
  key: string | null;
  energy: number | null;
  genre: string[] | null;
  subgenre: string | null;
  moods: string[] | null; // JSONB on DB but deserialized as array
  instruments: string[] | null;
  tags: string[] | null;
  vocal_gender: string | null;
  vocal_type: string | null;
  language: string | null;
  cultural_fusion: string | null;
  lyrical_theme: string | null;
  emotional_arc: string | null;
  harmony: string | null;
  chord_progression: string | null;
  historical_period: string | null;
  // Short human-readable summary shown in the Description block (mirrors tracks.description).
  description: string | null;
  // Verbose sonic-fingerprint text that feeds the search embedding — NOT the UI Description.
  detailed_analysis: string | null;
  sync_context: string | null;
  analysis_method: string | null;
  clap_success: boolean | null;
  confidence_scores: Record<string, unknown> | null;

  ingest_status: "pending" | "ok" | "skipped" | "error";
  ingest_error: string | null;
  analysis_status:
    | "pending"
    | "queued"
    | "analyzing"
    | "done"
    | "error"
    | "error_embedding"
    | "error_audio_analysis"
    | "queued_tomorrow";
  analysis_error: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface PlaylistTrackRow {
  id: string;
  playlist_id: string;
  reference_track_id: string | null;
  track_id: string | null;
  source: string;
  position: number;
  reference_tracks: ReferenceTrack | null;
}

export interface ReferencePlaylistDetail extends ReferencePlaylistListItem {
  tracks: PlaylistTrackRow[];
}

export async function getReferencePlaylist(
  playlistId: string
): Promise<ReferencePlaylistDetail> {
  return apiClient.get<ReferencePlaylistDetail>(
    `/api/reference-imports/playlists/${playlistId}`
  );
}

// ── POST /api/reference-imports/playlists/{id}/retry-import ──────────────────

export async function retryPlaylistImport(
  playlistId: string
): Promise<{ playlist_id: string; import_status: string }> {
  return apiClient.post<{ playlist_id: string; import_status: string }>(
    `/api/reference-imports/playlists/${playlistId}/retry-import`
  );
}

// ── POST /api/reference-imports/tracks/{id}/retry-analysis ───────────────────

export async function retryTrackAnalysis(
  trackId: string
): Promise<{ track_id: string; analysis_status: string }> {
  return apiClient.post<{ track_id: string; analysis_status: string }>(
    `/api/reference-imports/tracks/${trackId}/retry-analysis`
  );
}

// ── PATCH /api/reference-imports/tracks/{id} ──────────────────────────────────

export interface ReferenceTrackPatch {
  title?: string;
  artist?: string;
  genre?: string[];
  subgenre?: string;
  moods?: string[];
  instruments?: string[];
  vocal_gender?: string;
  vocal_type?: string;
  language?: string;
  cultural_fusion?: string;
  lyrical_theme?: string;
  emotional_arc?: string;
  harmony?: string;
  chord_progression?: string;
  historical_period?: string;
  detailed_analysis?: string;
  tags?: string[];
  sync_context?: string;
}

export async function patchReferenceTrack(
  trackId: string,
  patch: ReferenceTrackPatch
): Promise<{ track_id: string; updated: boolean; re_embed: boolean }> {
  return apiClient.patch<{ track_id: string; updated: boolean; re_embed: boolean }>(
    `/api/reference-imports/tracks/${trackId}`,
    patch
  );
}

// ── DELETE /api/reference-imports/playlists/{id} ──────────────────────────────

export async function deleteReferencePlaylist(
  playlistId: string
): Promise<{ playlist_id: string; deleted: boolean }> {
  return apiClient.delete<{ playlist_id: string; deleted: boolean }>(
    `/api/reference-imports/playlists/${playlistId}`
  );
}

// ── GET /api/reference-imports/usage ─────────────────────────────────────────

export interface UsageResponse {
  org_id: string;
  daily_analyzed: number;
  daily_soft_limit: number;
  daily_hard_limit: number;
  playlist_count: number;
  track_count: number;
}

export async function getReferenceUsage(): Promise<UsageResponse> {
  return apiClient.get<UsageResponse>("/api/reference-imports/usage");
}

// ── GET /api/reference-imports/share/{share_token} ───────────────────────────

/**
 * Flat per-track shape returned by the share endpoint (_project_share_track).
 * The backend deliberately EXCLUDES audio_storage_path (PII) and returns
 * fields at the top level — no nested reference_tracks object.
 */
export interface ShareReferenceTrack {
  position: number;
  id: string;
  title: string;
  artist: string | null;
  album: string | null;
  duration_sec: number | null;
  isrc: string | null;
  genre: string[] | null;
  subgenre: string | null;
  moods: string[] | null;
  instruments: string[] | null;
  vocal_type: string | null;
  bpm: number | null;
  tempo: string | null;
  energy: number | null;
  emotional_arc: string | null;
  sync_context: string | null;
  description: string | null;
  detailed_analysis: string | null;
  ingest_status: string;
  analysis_status: string;
}

/**
 * Narrow playlist metadata returned by the share endpoint.
 * The backend only returns { name, cover_image_url, import_status } —
 * NOT the full ReferencePlaylistListItem (no id, created_at, source_url, etc.).
 */
export interface SharedReferencePlaylistMeta {
  name: string;
  cover_image_url: string | null;
  import_status: string | null;
}

export interface SharedReferencePlaylistResponse {
  playlist: SharedReferencePlaylistMeta;
  tracks: ShareReferenceTrack[];
}

export async function getSharedReferencePlaylist(
  shareToken: string,
  password?: string
): Promise<SharedReferencePlaylistResponse> {
  const params = password ? `?password=${encodeURIComponent(password)}` : "";
  return apiClient.get<SharedReferencePlaylistResponse>(
    `/api/reference-imports/share/${shareToken}${params}`
  );
}

// ── Limit constants (mirrored from spec §4 and backend constants.py) ──────────
// These are used for pre-flight checks in the dialog (L6/L7/L8).

/** L7: Max total download size per import (10 GB). */
export const MAX_IMPORT_BYTES = 10 * 1024 * 1024 * 1024;

/** L6: Max per-track audio file size (200 MB). */
export const MAX_TRACK_BYTES = 200 * 1024 * 1024;

/** L8: Max per-track duration (30 minutes in seconds). */
export const MAX_TRACK_DURATION_SEC = 30 * 60;

// ── Pre-flight check helpers ──────────────────────────────────────────────────

export interface PreflightResult {
  /** True = import can proceed. False = confirm button must be disabled. */
  canProceed: boolean;
  /** Tracks that violate L6 (oversized). Import will skip them. */
  oversizedTracks: DiscoPreviewTrack[];
  /** Tracks that violate L8 (over 30 min). Import will skip them. */
  overDurationTracks: DiscoPreviewTrack[];
  /** True if total estimated bytes exceed L7 (10 GB). Blocks the whole import. */
  totalBytesExceeded: boolean;
  /** True if the playlist is not downloadable (no download access). */
  notDownloadable: boolean;
  /** True if share_mode is not 'standard'. */
  nonStandardShareMode: boolean;
}

/**
 * Run L6/L7/L8 pre-flight checks against a preview payload.
 *
 * canProceed is false when:
 *   - The playlist is not downloadable (L6 variant)
 *   - Total bytes exceed 10 GB (L7)
 *
 * Oversized/over-duration individual tracks do NOT block the import;
 * they are shown as warnings and will be skipped by the worker.
 */
export function preflightCheck(preview: DiscoPreviewResponse): PreflightResult {
  const notDownloadable = !preview.downloadable;
  const nonStandardShareMode = preview.share_mode !== "standard";
  const totalBytesExceeded = preview.total_bytes_estimate > MAX_IMPORT_BYTES;

  const oversizedTracks = preview.tracks.filter((t) => {
    // Prefer size_bytes (backend FINAL contract, I2); fall back to parsing pretty_size.
    const sizeBytes = t.size_bytes > 0 ? t.size_bytes : parsePrettySizeToBytes(t.pretty_size);
    return sizeBytes !== null && sizeBytes > MAX_TRACK_BYTES;
  });

  const overDurationTracks = preview.tracks.filter(
    (t) => t.duration > 0 && t.duration > MAX_TRACK_DURATION_SEC
  );

  const canProceed = !notDownloadable && !totalBytesExceeded && !nonStandardShareMode;

  return {
    canProceed,
    oversizedTracks,
    overDurationTracks,
    totalBytesExceeded,
    notDownloadable,
    nonStandardShareMode,
  };
}

/**
 * Parse a Disco pretty_size string (e.g. "10.9 MB", "1.2 GB") to bytes.
 * Returns null if the format is unrecognized.
 */
function parsePrettySizeToBytes(pretty: string): number | null {
  if (!pretty) return null;
  const match = pretty.trim().match(/^([\d.]+)\s*(KB|MB|GB|TB)$/i);
  if (!match) return null;
  const value = parseFloat(match[1]);
  const unit = match[2].toUpperCase();
  const multipliers: Record<string, number> = {
    KB: 1024,
    MB: 1024 * 1024,
    GB: 1024 * 1024 * 1024,
    TB: 1024 * 1024 * 1024 * 1024,
  };
  return value * (multipliers[unit] ?? 1);
}

export { parsePrettySizeToBytes };

// ── POST /api/reference-imports/playlists/{id}/share ─────────────────────────

export interface DiscoShareOptions {
  password?: string;
  expires_at?: string;
  max_uses?: number;
  max_views?: number;
}

export interface DiscoShareResponse {
  share_token: string;
  url_path: string; // "/playlist/share/<token>"
}

export async function createDiscoShare(
  playlistId: string,
  opts?: DiscoShareOptions
): Promise<DiscoShareResponse> {
  return apiClient.post<DiscoShareResponse>(
    `/api/reference-imports/playlists/${playlistId}/share`,
    opts ?? {}
  );
}

// ── DELETE /api/reference-imports/playlists/{id}/share/{token} ───────────────

export interface DiscoShareRevokeResponse {
  playlist_id: string;
  share_token: string;
  revoked: boolean;
}

export async function revokeDiscoShare(
  playlistId: string,
  shareToken: string
): Promise<DiscoShareRevokeResponse> {
  return apiClient.delete<DiscoShareRevokeResponse>(
    `/api/reference-imports/playlists/${playlistId}/share/${shareToken}`
  );
}
